/**
 * Coach mode: the Hard bot, sitting in the player's chair, saying what it
 * would do.
 *
 * One advice at a time, for the position in front of the player, keyed by the
 * snapshot's `seq` — the position cannot change without it moving. It is module
 * state rather than component state because the hand band, the bid panel and
 * the coach sheet all want the same answer, and none of them should start a
 * second search for it.
 *
 * The search runs in a worker, on this device, from the redacted snapshot the
 * server already sent. So it adds nothing to the server's load, and — the part
 * that matters for anyone sitting at the table with a coached player — it
 * cannot know a single card the player could not see.
 */
import { useEffect, useState } from 'react'
import { cardLabel, type BotAction, type CardId, type GameTypes, type Suit } from '@cards/shared'
import type { TableView } from '../table/view'
import type { LegalCall } from '../games/types'
import { useSettings } from '../settings/settings'
import type { CoachAdvice, CoachRequest, CoachResponse } from './protocol'

export type CoachState =
  | { readonly key: string; readonly status: 'thinking' }
  | { readonly key: string; readonly status: 'ready'; readonly advice: CoachAdvice }
  | { readonly key: string; readonly status: 'error' }

/** Phones are slower than the server and a player is waiting on it, so the
 *  coach looks at a few more worlds than the bot but stops on the clock. */
const BUDGET = { maxWorlds: 64, budgetMs: 1200 }

let current: CoachState | null = null
let worker: Worker | null = null
let inFlight: string | null = null
const subscribers = new Set<(s: CoachState | null) => void>()

function publish(next: CoachState | null): void {
  current = next
  for (const fn of subscribers) fn(next)
}

function onReply(reply: CoachResponse): void {
  const key = reply.ok ? reply.advice.key : reply.key
  if (key !== inFlight) return
  inFlight = null
  if (current?.key !== key) return
  publish(reply.ok ? { key, status: 'ready', advice: reply.advice } : { key, status: 'error' })
}

function getWorker(): Worker | null {
  if (worker) return worker
  if (typeof Worker === 'undefined') return null
  try {
    worker = new Worker(new URL('./coach.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (e: MessageEvent<CoachResponse>) => onReply(e.data)
    worker.onerror = () => {
      if (inFlight && current?.key === inFlight) publish({ key: inFlight, status: 'error' })
      inFlight = null
    }
  } catch {
    worker = null
  }
  return worker
}

function ask(req: CoachRequest): void {
  if (current?.key === req.key) return
  // A position that has moved on is not worth finishing: stop the old search
  // rather than queue the new one behind it.
  if (inFlight !== null && worker) {
    worker.terminate()
    worker = null
  }
  publish({ key: req.key, status: 'thinking' })
  const w = getWorker()
  if (!w) {
    publish({ key: req.key, status: 'error' })
    return
  }
  inFlight = req.key
  w.postMessage(req)
}

/** The request this view warrants, or null when there is nothing to advise. */
export function coachRequestOf(view: TableView): CoachRequest | null {
  const match = view.match
  const actions = view.actions
  const hand = view.hand
  if (!match || !hand || !actions || match.status.kind === 'finished') return null
  if (view.mySeat === null) return null
  const mine = actions.pending !== null && actions.controls.includes(actions.pending.seat)
  const owes = mine || (actions.toAct !== null && (actions.legalPlays.length > 0 || actions.legalCalls.length > 0))
  if (!owes) return null
  return {
    key: `${match.matchId}:${view.snapshot.seq}:${actions.toAct ?? ''}`,
    gameId: match.gameId,
    state: match,
    seat: view.mySeat,
    actions,
    budget: BUDGET,
  }
}

/** The host allowed coach mode at this table (lobby only, fixed once dealt). */
export function coachAllowed(view: TableView): boolean {
  return view.snapshot.room.coachAllowed === true
}

/** Coach advice for the position in `view`, or null when coach mode is off —
 *  at this table, or by this player's own choice — or nothing is owed. */
export function useCoach(view: TableView): CoachState | null {
  const [settings] = useSettings()
  const [state, setState] = useState<CoachState | null>(current)
  useEffect(() => {
    subscribers.add(setState)
    setState(current)
    return () => {
      subscribers.delete(setState)
    }
  }, [])
  const req = settings.coach && coachAllowed(view) ? coachRequestOf(view) : null
  const key = req?.key ?? null
  useEffect(() => {
    if (req) ask(req)
    // Keyed on the position, not on the request object, which is new on
    // every render.
  }, [key])
  if (!key || !state || state.key !== key) return key ? { key, status: 'thinking' } : null
  return state
}

/** The card the coach would play, when its advice is a card. */
export function coachCard(state: CoachState | null): { card: CardId; nominate?: Suit } | null {
  if (state?.status !== 'ready') return null
  const a = state.advice.action
  return a.kind === 'play' ? { card: a.card, nominate: a.nominate } : null
}

/** The legal call the coach would make, when its advice is a call. */
export function coachCall(view: TableView, state: CoachState | null): LegalCall | null {
  if (state?.status !== 'ready') return null
  const a = state.advice.action
  if (a.kind !== 'call') return null
  const want = JSON.stringify(a.call)
  return ((view.actions?.legalCalls ?? []) as readonly LegalCall[]).find((c) => JSON.stringify(c.call) === want) ?? null
}

/** A short imperative for the advice: "Play Q♠", "Bid 7♥", "Pass". */
export function adviceHeadline(view: TableView, action: BotAction<GameTypes>): string {
  switch (action.kind) {
    case 'play': {
      const nom = action.nominate ? ` and name ${SUIT[action.nominate]}` : ''
      return `Play ${cardLabel(action.card)}${nom}`
    }
    case 'call': {
      const call = ((view.actions?.legalCalls ?? []) as readonly LegalCall[]).find(
        (c) => JSON.stringify(c.call) === JSON.stringify(action.call),
      )
      if (!call) return 'Call'
      return view.gameUi.isPass(call) ? 'Pass' : `Bid ${call.label}`
    }
    case 'decide': {
      const d = action.decision as { cards?: readonly CardId[]; suit?: Suit | null } | null
      if (d && Array.isArray(d.cards)) return `Discard ${d.cards.map(cardLabel).join(' ')}`
      if (d && 'suit' in d) return d.suit ? `Name ${SUIT[d.suit]}` : 'Leave it floating'
      return 'Decide'
    }
    default:
      return 'Wait'
  }
}

const SUIT: Record<Suit, string> = { S: '♠', H: '♥', D: '♦', C: '♣' }
